import { Link, useParams } from 'react-router-dom';
import { useExternalLead } from '../../hooks/useExternalLeads';
import { Spinner } from '../../components/common/Spinner';
import { Button } from '../../components/common/Button';
import { ExternalApiError } from '../../services/api/externalAuth';
import { formatDisplayDate } from '../../utils/format';
import { labelTextColor } from '../../utils/labelColor';

/**
 * External Lead detail (Phase 105 SC-2 / D-02 / D-06 / ADR 0005).
 *
 * Read-only external surface: this page MUST NOT import the internal
 * auth-context module or its hook (v4.0 Landmine #8).
 *
 * D-02 is a full-payload decision: every field the backend response carries
 * is surfaced here on purpose — deal value, per-role hourly rates, and note
 * content/authors included. This is the exposure ADR 0005 documents as
 * accepted, not an oversight. Do NOT strip fields here to "be safe"; if the
 * exposure is later judged too broad, the narrowing happens at the API
 * layer (backend/src/services/externalLeadService.ts), not by quietly
 * hiding fields in this component.
 *
 * No mutation affordance: no note-authoring input, no stage-change control,
 * no edit/delete button anywhere on this page.
 */
export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const numericId = Number(id);
  const leadQuery = useExternalLead(numericId);

  if (leadQuery.isLoading && !leadQuery.data) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner />
      </div>
    );
  }

  const err = leadQuery.error as ExternalApiError | null;

  // 403 (revoked/never-held grant) — returned BEFORE any row-rendering path
  // so a stale cached lead (T-105-CACHE) can never render alongside the error.
  if (err instanceof ExternalApiError && err.statusCode === 403) {
    return (
      <div className="text-center py-12">
        <h1 className="text-lg font-semibold text-gray-900">Access no longer available</h1>
        <p className="mt-2 text-sm text-gray-500">
          Lead access is no longer available for your account. Contact the person who invited
          you if you believe this is a mistake.
        </p>
      </div>
    );
  }

  if (err instanceof ExternalApiError && err.statusCode === 404) {
    return (
      <div className="text-center py-12">
        <h1 className="text-lg font-semibold text-gray-900">Lead not found</h1>
        <p className="mt-2 text-sm text-gray-500">This lead does not exist or was removed.</p>
        <Link to="/external/leads" className="mt-3 inline-block text-sm text-primary-600 hover:underline">
          &larr; Back to leads
        </Link>
      </div>
    );
  }

  if (err) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-700">Couldn&apos;t load this lead — try refreshing.</p>
        <Button
          variant="secondary"
          size="sm"
          className="mt-3"
          onClick={() => leadQuery.refetch()}
        >
          Retry
        </Button>
      </div>
    );
  }

  const lead = leadQuery.data;
  if (!lead) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No lead data.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <Link to="/external/leads" className="text-sm text-primary-600 hover:underline">
        &larr; Back to leads
      </Link>

      <div className="mt-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{lead.name}</h1>
        <p className="text-sm text-gray-500">{lead.customer?.name ?? '—'}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="inline-block rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
            {lead.stage}
          </span>
          {lead.labels.map((l) => (
            <span
              key={l.id}
              style={{ backgroundColor: l.color, color: labelTextColor(l.color) }}
              className="inline-block rounded px-1.5 py-0.5 text-[10px] font-medium leading-tight"
            >
              {l.name}
            </span>
          ))}
        </div>
      </div>

      {/* MKS-344 — the reusable contacts, read-only like everything on this
          page (there is no write route on `/api/external/*` at all). Rendered
          only when the lead has any: until the backfill has run, or for a lead
          nobody has given contacts, the three legacy fields below are still
          the whole answer. Ungated — an external-portal-only change needs no
          release flag (docs/public/RELEASE-FLAGS.md), and the viewer already sees
          this same data as the three fields. */}
      {lead.contacts && lead.contacts.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Contacts</h2>
          <ul className="mt-2 space-y-2">
            {lead.contacts.map((c) => (
              <li key={c.id} className="text-sm text-gray-900">
                <span className="font-medium">{c.name}</span>
                {c.isPrimary && (
                  <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-gray-600">
                    Primary
                  </span>
                )}
                {(c.email || c.phone) && (
                  <span className="block text-xs text-gray-500">
                    {c.email && (
                      <a href={`mailto:${c.email}`} className="text-primary-600 hover:text-primary-700">
                        {c.email}
                      </a>
                    )}
                    {c.email && c.phone && ' · '}
                    {c.phone && (
                      <a href={`tel:${c.phone}`} className="text-primary-600 hover:text-primary-700">
                        {c.phone}
                      </a>
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* MKS-346 — the SLA pricing configurations priced for this lead, name
          and monthly price, READ ONLY. No link to the configurator: that
          surface is internal-identity + requireSales only (Phase 107 D-03 /
          SLA-PRICE-04), so a link from here would be a dead end at best.
          Rendered only when the lead has any, which is also why this section
          needs no release flag: `useFlag` is permanently false for an external
          actor (FlagsProvider is fail-closed outside an internal session,
          D-120-10), and nothing can link a configuration until the internal
          flag is on — so with the flag off no lead has any and this renders
          nothing. Same ungated-because-external-portal-only reasoning as the
          contacts block above (docs/public/RELEASE-FLAGS.md).

          The price string is printed verbatim: the backend emits a fixed-2
          decimal and this page does no arithmetic on money, matching the
          `dealValue` row below. */}
      {lead.slaPricingConfigurations && lead.slaPricingConfigurations.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            SLA pricing
          </h2>
          <ul className="mt-2 space-y-2">
            {lead.slaPricingConfigurations.map((config) => (
              <li key={config.id} className="text-sm text-gray-900">
                <span className="font-medium">{config.name}</span>
                <span className="block font-mono tabular-nums text-xs text-gray-500">
                  {config.monthlyPrice} {config.currency} / month
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* D-02 full payload — contact, deal, timeline, ownership, project links */}
      <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 rounded-lg border border-gray-200 bg-white p-4">
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">Contact person</dt>
          <dd className="text-sm text-gray-900">{lead.contactPerson ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">Contact email</dt>
          <dd className="text-sm text-gray-900">{lead.contactEmail ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">Contact phone</dt>
          <dd className="text-sm text-gray-900">{lead.contactPhone ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">Deal value</dt>
          <dd className="text-sm text-gray-900">
            {lead.dealValue ? `${lead.dealValue} ${lead.dealValueCurrency}` : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">Payment term</dt>
          <dd className="text-sm text-gray-900">
            {lead.paymentTerm ? `${lead.paymentTerm.name} (${lead.paymentTerm.days} days)` : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">Source</dt>
          <dd className="text-sm text-gray-900">{lead.source?.name ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">Expected close</dt>
          <dd className="text-sm text-gray-900">{formatDisplayDate(lead.expectedCloseDate)}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">Expected start</dt>
          <dd className="text-sm text-gray-900">{formatDisplayDate(lead.expectedStartDate)}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">Expected end</dt>
          <dd className="text-sm text-gray-900">{formatDisplayDate(lead.expectedEndDate)}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">Owner</dt>
          <dd className="text-sm text-gray-900">
            {lead.owner.firstName} {lead.owner.lastName}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">Linked projects</dt>
          {/* Names only, NOT links — the external user has no route to an
              internal project page. */}
          <dd className="text-sm text-gray-900">
            {[lead.presalesProject?.name, lead.convertedProject?.name, lead.nonBillableProject?.name]
              .filter(Boolean)
              .join(', ') || '—'}
          </dd>
        </div>
      </dl>

      {/* Roles */}
      <div className="mt-6">
        <h2 className="text-sm font-semibold text-gray-900 mb-2">Roles</h2>
        {lead.leadRoles.length === 0 ? (
          <p className="text-sm text-gray-500">No roles.</p>
        ) : (
          <div className="overflow-auto rounded-lg border border-gray-200 bg-white">
            <table className="divide-y divide-gray-200 w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Role
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Headcount
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Hourly rate
                  </th>
                  <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Currency
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {lead.leadRoles.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-2 text-sm text-gray-900">{r.role.name}</td>
                    <td className="px-4 py-2 text-sm text-gray-500">{r.headcount}</td>
                    <td className="px-4 py-2 text-sm text-gray-500">{r.hourlyRate ?? '—'}</td>
                    <td className="px-4 py-2 text-sm text-gray-500">{r.currency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Notes — display only, no note-authoring input */}
      <div className="mt-6">
        <h2 className="text-sm font-semibold text-gray-900 mb-2">Notes</h2>
        {lead.leadNotes.length === 0 ? (
          <p className="text-sm text-gray-500">No notes.</p>
        ) : (
          <ul className="space-y-3">
            {lead.leadNotes.map((note) => (
              <li key={note.id} className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{note.content}</p>
                <p className="mt-1 text-xs text-gray-400">
                  {note.author.firstName} {note.author.lastName} &middot;{' '}
                  {formatDisplayDate(note.createdAt)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
